import React, { useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, X } from 'lucide-react';

/**
 * The Ctrl+F bar above the DB Manager grid.
 *
 * Deliberately a strip rather than a floating overlay: the grid is dense and a
 * panel sitting on top of it would cover the rows you are searching.
 */
export default function DbGridFindBar({
  query,
  onQueryChange,
  matchCase,
  onMatchCaseChange,
  matchCount,
  activeIndex,
  truncated,
  onNext,
  onPrev,
  onClose,
  focusNonce = 0,
}) {
  const inputRef = useRef(null);

  // Opening the bar should let you type immediately, and pressing Ctrl+F again
  // with it already open should select what is there so a new search replaces
  // the old one — both are the editor behaviour people expect.
  // focusNonce moves on every Ctrl+F while the bar is open (was a global
  // document.querySelector, which found the first bar in the document).
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [focusNonce]);

  // The bar's buttons must not take focus from the input: Enter / Shift+Enter
  // only step while the input has it (clicking Aa used to move focus to a
  // checkbox, and Enter then did nothing).
  const keepFocus = (e) => e.preventDefault();

  const hasQuery = query !== '';
  const status = !hasQuery
    ? ''
    : matchCount === 0
      ? 'No matches'
      : `${activeIndex + 1} of ${matchCount}${truncated ? '+' : ''}`;

  return (
    <div className="dbm-find" role="search">
      <input
        ref={inputRef}
        type="text"
        className="dbm-find-input"
        placeholder="Find in results"
        aria-label="Find in results"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) onPrev();
            else onNext();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          } else if (e.altKey && !e.ctrlKey && !e.metaKey && (e.code === 'KeyC' || (e.key || '').toLowerCase() === 'c')) {
            // Alt+C: match case, as in VS Code (e.code: with Alt held, e.key is layout-dependent).
            e.preventDefault();
            onMatchCaseChange(!matchCase);
          }
        }}
      />
      <span
        className={`dbm-find-status${hasQuery && matchCount === 0 ? ' dbm-find-status--none' : ''}`}
        role="status"
      >
        {status}
      </span>
      <button
        type="button"
        className="dbm-find-btn"
        onMouseDown={keepFocus}
        onClick={onPrev}
        disabled={matchCount === 0}
        title="Previous match (Shift+Enter)"
        aria-label="Previous match"
      >
        <ChevronUp size={14} />
      </button>
      <button
        type="button"
        className="dbm-find-btn"
        onMouseDown={keepFocus}
        onClick={onNext}
        disabled={matchCount === 0}
        title="Next match (Enter)"
        aria-label="Next match"
      >
        <ChevronDown size={14} />
      </button>
      <button
        type="button"
        className={`dbm-find-btn dbm-find-case${matchCase ? ' dbm-find-case--on' : ''}`}
        onMouseDown={keepFocus}
        onClick={() => onMatchCaseChange(!matchCase)}
        aria-pressed={matchCase}
        title="Match case (Alt+C)"
        aria-label="Match case"
      >
        Aa
      </button>
      {truncated && (
        <span className="dbm-find-note" title={`Only the first ${matchCount} matches are listed.`}>
          first {matchCount}
        </span>
      )}
      <button
        type="button"
        className="dbm-find-btn dbm-find-btn--close"
        onMouseDown={keepFocus}
        onClick={onClose}
        title="Close (Esc)"
        aria-label="Close find"
      >
        <X size={14} />
      </button>
    </div>
  );
}
